import { TrackingService } from '../services/TrackingService'
import { FieldSelected } from '../events/FieldSelected'
import type { UserInputField, StepContext, FormContext } from '../types'

export type FieldSelectedConfig = {
  userInputField: UserInputField
  stepContext: StepContext
  formContext: FormContext
}

/**
 * Hook for tracking field selection events
 *
 * Tracks when a user selects or interacts with a form field. This is essential for understanding
 * user behavior in forms and measuring field engagement and completion patterns.
 *
 * @returns A function that tracks field selection events
 *
 * @example
 * ```typescript
 * const trackFieldSelected = useFieldSelected()
 *
 * const handleFieldFocus = () => {
 *   trackFieldSelected({
 *     userInputField: {
 *       fieldName: 'email',
 *       fieldType: 'text',
 *       fieldValue: 'user@example.com',
 *       fieldSource: 'user-input',
 *       autofilled: false
 *     },
 *     stepContext: {
 *       stepId: 'step-1',
 *       stepName: 'Contact Information',
 *       stepNumber: 1
 *     },
 *     formContext: {
 *       formName: 'signup-form',
 *       formType: 'registration',
 *       formVersion: '1.0'
 *     }
 *   })
 * }
 * ```
 */
function useFieldSelected() {
  return function trackFieldSelected(config: FieldSelectedConfig): void {
    const fieldSelectedEvent: FieldSelected = new FieldSelected({
      formContext: {
        formName: config.formContext.formName,
        formType: config.formContext.formType,
        formVersion: config.formContext.formVersion,
        formId: config.formContext.formId,
      },
      stepContext: {
        stepId: config.stepContext.stepId,
        stepName: config.stepContext.stepName,
        stepNumber: config.stepContext.stepNumber,
      },
      userInputField: {
        fieldName: config.userInputField.fieldName,
        fieldType: config.userInputField.fieldType,
        fieldValue: config.userInputField.fieldValue,
        fieldSource: config.userInputField.fieldSource,
        autofilled: config.userInputField.autofilled,
      },
    })

    TrackingService.track(fieldSelectedEvent)
  }
}

export default useFieldSelected
